import type { Draft } from "@reduxjs/toolkit";
import { createSlice } from "@reduxjs/toolkit";
import { CusaC360b2bInterface } from "../../interface/redux-state/CusaC360b2bInterface";

const initialState: CusaC360b2bInterface = {
  cardBrand: "",
  ccExpYearMonth: null,
  avsName: "",
  avsAddress1: "",
  avsAddress2: "",
  avsCity: "",
  avsState: "",
  avsZip: "",
  avsCountryCode: "",
  amount: "",
  orderNumber: "",
  decryptParams: [],
  microformLoaded: false,
  validationMessage: "",
  formDisabled: false,
  proxyUrl: "",
  isDatePickerOpen: false,
  logErrorReference: "",
  initializationComplted: false,
  cvvCybrErrorMessage: "",
  cardCybrErrorMessage: "",
  dateCybrErrorMessage: "",
  nameCybrErrorMessage: "",
  processForm: false,
  error: {
    avsNameEmpty: true,
    cardInvalid: false,
    cardMissing: true,
    ccExpExpired: false,
    ccExpInvalid: false,
    ccExpMissing: true,
    cvvEmpty: true,
    cvvInvalid: false,
    cvvCybrError: false,
    cardCybrError: false,
    nameCybrError: false,
    dateCybrError: false,
  },
};

export const CusaC360b2bSlice = createSlice({
  name: "cusac360b2b",
  initialState,
  reducers: {
    resetStateAndError: (state: Draft<CusaC360b2bInterface>) => {
      state.cardBrand = initialState.cardBrand;
      state.ccExpYearMonth = initialState.ccExpYearMonth;
      state.avsName = initialState.avsName;
      state.avsAddress1 = initialState.avsAddress1;
      state.avsAddress2 = initialState.avsAddress2;
      state.avsCity = initialState.avsCity;
      state.avsState = initialState.avsState;
      state.avsZip = initialState.avsZip;
      state.avsCountryCode = initialState.avsCountryCode;
      state.amount = initialState.amount;
      state.orderNumber = initialState.orderNumber;
      state.decryptParams = initialState.decryptParams;
      state.microformLoaded = initialState.microformLoaded;
      state.validationMessage = initialState.validationMessage;
      state.formDisabled = initialState.formDisabled;
      state.proxyUrl = initialState.proxyUrl;
      state.isDatePickerOpen = initialState.isDatePickerOpen;
      state.logErrorReference = initialState.logErrorReference;
      state.initializationComplted = initialState.initializationComplted;
      state.cvvCybrErrorMessage = initialState.cvvCybrErrorMessage;
      state.cardCybrErrorMessage = initialState.cardCybrErrorMessage;
      state.dateCybrErrorMessage = initialState.dateCybrErrorMessage;
      state.nameCybrErrorMessage = initialState.nameCybrErrorMessage;
      state.processForm = initialState.processForm;
      state.error = initialState.error;
    },
    setCvv: (state: Draft<CusaC360b2bInterface>, action) => {
      state.error.cvvInvalid = !action.payload.cvvValid;
      state.error.cvvEmpty = action.payload.cvvEmpty;
      state.error.cvvCybrError = false;
      state.cvvCybrErrorMessage = "";
    },
    setCybrError: (state: Draft<CusaC360b2bInterface>, action) => {
      state.error.cvvCybrError = action.payload.cvvCybrError;
      state.cvvCybrErrorMessage = action.payload.cvvCybrErrorMessage;
      state.error.cardCybrError = action.payload.cardCybrError;
      state.cardCybrErrorMessage = action.payload.cardCybrErrorMessage;
      state.error.dateCybrError = action.payload.dateCybrError;
      state.dateCybrErrorMessage = action.payload.dateCybrErrorMessage;
      state.error.nameCybrError = action.payload.nameCybrError;
      state.nameCybrErrorMessage = action.payload.nameCybrErrorMessage;
    },
    setCardType: (state: Draft<CusaC360b2bInterface>, action) => {
      state.cardBrand = action.payload.cardBrand;
      state.error.cardInvalid = action.payload.cardInvalid;
      state.error.cardMissing = action.payload.cardMissing;
      state.error.cardCybrError = false;
      state.cardCybrErrorMessage = "";
    },
    setExpYearMonth: (state: Draft<CusaC360b2bInterface>, action) => {
      state.ccExpYearMonth = action.payload.ccExpYearMonth;
    },
    setExpYearMonthValid: (state: Draft<CusaC360b2bInterface>, action) => {
      state.error.ccExpExpired = action.payload.ccExpExpired;
      state.error.ccExpInvalid = action.payload.ccExpInvalid;
      state.error.ccExpMissing = action.payload.ccExpMissing;
      state.error.dateCybrError = false;
      state.dateCybrErrorMessage = "";
    },
    setAvsName: (state: Draft<CusaC360b2bInterface>, action) => {
      state.avsName = action.payload.avsName;
      state.error.avsNameEmpty = action.payload.avsNameEmpty;
      state.error.nameCybrError = false;
      state.nameCybrErrorMessage = "";
    },
    setBillingAddress: (state: Draft<CusaC360b2bInterface>, action) => {
      state.avsAddress1 = action.payload.avsAddress1;
      state.avsAddress2 = action.payload.avsAddress2;
      state.avsCity = action.payload.avsCity;
      state.avsState = action.payload.avsState;
      state.avsZip = action.payload.avsZip;
      state.avsCountryCode = action.payload.avsCountryCode;
      state.orderNumber = action.payload.orderNumber;
    },
    setDecryptParams: (state: Draft<CusaC360b2bInterface>, action) => {
      state.decryptParams = action.payload.decryptParams;
    },
    setAmount: (state: Draft<CusaC360b2bInterface>, action) => {
      state.amount = action.payload.amount;
    },
    setMicroformLoaded: (state: Draft<CusaC360b2bInterface>, action) => {
      state.microformLoaded = action.payload.microformLoaded;
    },
    setValidationMessage: (state: Draft<CusaC360b2bInterface>, action) => {
      state.validationMessage = action.payload.validationMessage;
    },
    setFormDisabled: (state: Draft<CusaC360b2bInterface>, action) => {
      state.formDisabled = action.payload.formDisabled;
    },
    setProxyUrl: (state: Draft<CusaC360b2bInterface>, action) => {
      state.proxyUrl = action.payload.proxyUrl;
    },
    setDatePickerOpen: (state: Draft<CusaC360b2bInterface>, action) => {
      state.isDatePickerOpen = action.payload.isDatePickerOpen;
    },
    setLogErrorReference: (state: Draft<CusaC360b2bInterface>, action) => {
      state.logErrorReference = action.payload.logErrorReference;
    },
    setInitializationComplted: (state: Draft<CusaC360b2bInterface>, action) => {
      state.initializationComplted = action.payload.initializationComplted;
    },
    setProcessForm: (state: Draft<CusaC360b2bInterface>, action) => {
      state.processForm = action.payload.processForm;
    },
  },
});

export const {
  resetStateAndError,
  setCvv,
  setCardType,
  setExpYearMonth,
  setExpYearMonthValid,
  setAvsName,
  setBillingAddress,
  setDecryptParams,
  setAmount,
  setMicroformLoaded,
  setValidationMessage,
  setFormDisabled,
  setProxyUrl,
  setDatePickerOpen,
  setLogErrorReference,
  setInitializationComplted,
  setCybrError,
  setProcessForm,
} = CusaC360b2bSlice.actions;

export default CusaC360b2bSlice;
